// src/pages/Voters.tsx
import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { useCampaignId } from "../hooks/useCampaignId";

/** Row definition from the view */
type Row = {
  voter_id: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  suffix: string | null;
  precinct: string | null;
  address_id: string | null;

  address1: string | null;
  city: string | null;
  county: string | null;
  state: string | null;
  zip: string | null;

  street_name: string | null;
  address_number: number | null;
};

const PAGE_SIZE = 50;

type SortDir = "asc" | "desc";
type SortSpec = { key: keyof Row; dir: SortDir };

type FilterMode = "contains" | "equals";
type FilterSpec = { col: keyof Row; mode: FilterMode; val: string };

/** Filterable + Sortable columns */
const FILTERABLE_COLUMNS: Array<{ key: keyof Row; label: string }> = [
  { key: "voter_id", label: "Voter ID" },
  { key: "first_name", label: "First Name" },
  { key: "middle_name", label: "Middle Name" },
  { key: "last_name", label: "Last Name" },
  { key: "street_name", label: "Street Name" },
  { key: "city", label: "City" },
  { key: "county", label: "County" },
  { key: "state", label: "State" },
  { key: "zip", label: "ZIP" },
  { key: "precinct", label: "Precinct" },
];

const SORTABLE_COLUMNS: Array<{ key: keyof Row; label: string }> = [
  { key: "voter_id", label: "Voter ID" },
  { key: "first_name", label: "First Name" },
  { key: "middle_name", label: "Middle Name" },
  { key: "last_name", label: "Last Name" },
  { key: "address_number", label: "Number" },
  { key: "street_name", label: "Street Name" },
  { key: "city", label: "City" },
  { key: "county", label: "County" },
  { key: "state", label: "State" },
  { key: "zip", label: "ZIP" },
  { key: "precinct", label: "Precinct" },
];

function normalizeError(e: unknown, stage?: string) {
  let text = "";
  try {
    if (e && typeof e === "object") {
      const anyE: any = e;
      const orig = anyE.original ?? anyE;
      const parts: string[] = [];
      if (stage) parts.push(`stage=${stage}`);
      const msg = typeof orig.message === "string" ? orig.message.trim() : "";
      if (msg) parts.push(msg);
      if (typeof orig.code === "string") parts.push(`code=${orig.code}`);
      if (typeof orig.details === "string" && orig.details.trim()) parts.push(`details=${orig.details.trim()}`);
      if (typeof orig.hint === "string" && orig.hint.trim()) parts.push(`hint=${orig.hint.trim()}`);
      if (orig.status != null) parts.push(`status=${orig.status}`);
      text = parts.join(" | ") || "Unknown error (empty message)";
    } else if (typeof e === "string") {
      text = e || "Unknown error (empty string)";
    } else {
      text = String(e) || "Unknown error";
    }
  } catch {
    text = "Unknown error (failed to normalize)";
  }
  return { text, meta: e };
}
async function withStage<T>(stage: string, fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw { stage, original: error };
  }
}

/** Cursor stack for keyset pagination (forward-only) */
type Cursor = { after?: string };

export default function Voters() {
  const campaignId = useCampaignId();

  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<{ text: string; meta?: any } | null>(null);

  // multiple filters
  const [filters, setFilters] = useState<FilterSpec[]>([]);
  const [filterCol, setFilterCol] = useState<keyof Row>("last_name");
  const [filterMode, setFilterMode] = useState<FilterMode>("contains");
  const [filterVal, setFilterVal] = useState("");

  // primary sort is voter_id asc (required for keyset)
  const [sorts, setSorts] = useState<SortSpec[]>([{ key: "voter_id", dir: "asc" }]);
  const [newSortCol, setNewSortCol] = useState<keyof Row>("street_name");
  const [newSortDir, setNewSortDir] = useState<SortDir>("asc");

  // cursor-based paging
  const [pages, setPages] = useState<Cursor[]>([{ after: undefined }]);
  const pageIndex = pages.length - 1;
  const cursor = pages[pageIndex];

    function resetPaging() {
    setPages([{ after: undefined }]);
  }

  function addFilter() {
    const v = filterVal.trim();
    if (!v) return;
    setFilters((prev) => [...prev, { col: filterCol, mode: filterMode, val: v.toUpperCase() }]);
    setFilterVal("");
    resetPaging();
  }
  function removeFilter(idx: number) {
    setFilters((prev) => prev.filter((_, i) => i !== idx));
    resetPaging();
  }
  function clearFilters() {
    setFilters([]);
    setFilterVal("");
    resetPaging();
  }

  function removeSort(idx: number) {
    setSorts((prev) => prev.filter((_, i) => i !== idx));
    resetPaging();
  }
  function clearSorts() {
    setSorts([{ key: "voter_id", dir: "asc" }]);
    resetPaging();
  }

  function prevPage() {
    if (pages.length > 1) setPages((p) => p.slice(0, -1));
  }
  function nextPage(lastVoterId: string | undefined) {
    if (!lastVoterId) return;
    setPages((p) => [...p, { after: lastVoterId }]);
  }

  useEffect(() => {
    if (!campaignId) return;

    (async () => {
      try {
        setLoading(true);
        setErr(null);

        // --- COUNT ---
        // IMPORTANT: avoid head:true (it caused 500). Use limit(0) to get count.
        let qCount = supabase
          .from("campaign_voters_with_address")
          .select("voter_id", { count: "exact" })
          .eq("campaign_id", campaignId)
          .limit(0);

        for (const f of filters) {
          if (f.mode === "equals") qCount = qCount.eq(String(f.col), f.val);
          else qCount = qCount.ilike(String(f.col), `%${f.val}%`);
        }

        const { count: totalCount, error: countErr } = await withStage("count-view", async () => qCount);
        if (countErr) throw { ...countErr, stage: "count-view.result" };
        setTotal(totalCount ?? 0);

        // --- PAGE (keyset) ---
        // Step 1: voter_ids from base table using a cursor on voter_id
        let qIds = supabase
          .from("campaign_voters")
          .select("voter_id")
          .eq("campaign_id", campaignId)
          .order("voter_id", { ascending: true })
          .limit(PAGE_SIZE);

        if (cursor.after) qIds = qIds.gt("voter_id", cursor.after);

        const { data: idRows, error: idErr } = await withStage("page-ids", async () => qIds);
        if (idErr) throw { ...idErr, stage: "page-ids.result" };

        const ids: string[] = (idRows ?? []).map((r: any) => r.voter_id);
        if (ids.length === 0) {
          setRows([]);
          setLoading(false);
          return;
        }

        // Step 2: pull full rows from the view for those ids
        let qPage = supabase
          .from("campaign_voters_with_address")
          .select(
            "voter_id, first_name, middle_name, last_name, suffix, precinct, address_id, address1, city, county, state, zip, street_name, address_number"
          )
          .eq("campaign_id", campaignId)
          .in("voter_id", ids)
          .order("voter_id", { ascending: true });

        // apply filters to the view rows (if any)
        for (const f of filters) {
          if (f.mode === "equals") qPage = qPage.eq(String(f.col), f.val);
          else qPage = qPage.ilike(String(f.col), `%${f.val}%`);
        }

        const { data, error } = await withStage("page-view-by-ids", async () => qPage);
        if (error) throw { ...error, stage: "page-view-by-ids.result" };

        // Keep original id order
        const orderMap = new Map(ids.map((id, i) => [id, i]));
        const ordered = ((data ?? []) as Row[]).slice().sort(
          (a, b) => (orderMap.get(a.voter_id) ?? 0) - (orderMap.get(b.voter_id) ?? 0)
        );

        setRows(ordered);
      } catch (e: any) {
        setErr(normalizeError(e, e?.stage));
      } finally {
        setLoading(false);
      }
    })();
  }, [campaignId, cursor, filters]); // note: sorts beyond voter_id are client-side now

  function displayStreet(r: Row) {
    if (r.street_name) return r.street_name;
    const a1 = r.address1 || "";
    return a1.replace(/^\s*\d+[A-Za-z\-]*\s+/, "").trim();
  }
  function displayNumber(r: Row) {
    if (r.address_number != null) return String(r.address_number);
    const a1 = r.address1 || "";
    const m = a1.match(/^\s*([0-9]+)/);
    return m ? m[1] : "";
  }

  return (
    <div style={{ padding: 16 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Voters</h1>

      {err && (
        <div
          style={{
            background: "#ffe8e8",
            border: "1px solid #f2b0b0",
            padding: 8,
            borderRadius: 6,
            marginBottom: 10,
            color: "#a40000",
            whiteSpace: "pre-wrap",
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: 12,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{err.text || "Unknown error"}</div>
        </div>
      )}

      {/* Controls */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginBottom: 12 }}>
        {/* Filter builder (multi) */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ width: 80 }}>Filter</span>
            <select
              value={filterCol}
              onChange={(e) => setFilterCol(e.target.value as keyof Row)}
              style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 180 }}
            >
              {FILTERABLE_COLUMNS.map((c) => (
                <option key={String(c.key)} value={String(c.key)}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>

          <select
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value as FilterMode)}
            style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 140 }}
          >
            <option value="contains">contains</option>
            <option value="equals">equals</option>
          </select>

          <input
            placeholder="TYPE IN CAPS..."
            value={filterVal}
            onChange={(e) => setFilterVal(e.target.value.toUpperCase())}
            style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 240, textTransform: "uppercase" }}
          />

          <button
            onClick={addFilter}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", background: "#fff" }}
          >
            Add
          </button>

          <button
            onClick={clearFilters}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", background: "#fff" }}
          >
            Clear
          </button>
        </div>

        {/* Active filter chips */}
        {filters.length > 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {filters.map((f, idx) => (
              <span
                key={`${f.col}-${idx}-${f.val}`}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "4px 8px",
                  border: "1px solid #ddd",
                  borderRadius: 999,
                  background: "#f9fafb",
                }}
                title={`Filter ${idx + 1}`}
              >
                <strong>{FILTERABLE_COLUMNS.find((c) => c.key === f.col)?.label ?? String(f.col)}</strong>{" "}
                {f.mode === "equals" ? "=" : "∋"} {f.val}
                <button
                  onClick={() => removeFilter(idx)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, marginLeft: 4, lineHeight: 1 }}
                  aria-label="Remove filter"
                  title="Remove"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Sort builder (secondary sorts currently client-side after page fetch) */}
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ width: 80 }}>Sort by</span>

          <select
            value={newSortCol as string}
            onChange={(e) => setNewSortCol(e.target.value as keyof Row)}
            style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 200 }}
          >
            {SORTABLE_COLUMNS.map((c) => (
              <option key={String(c.key)} value={String(c.key)}>
                {c.label}
              </option>
            ))}
          </select>

          <select
            value={newSortDir}
            onChange={(e) => setNewSortDir(e.target.value as SortDir)}
            style={{ padding: 8, border: "1px solid #ddd", borderRadius: 8, minWidth: 140 }}
          >
            <option value="asc">ascending</option>
            <option value="desc" disabled>
              descending (disabled for keyset)
            </option>
          </select>

          <button
            onClick={() => {
              // only add secondary sorts for client-side ordering of the 50-row page
              setSorts((prev) => [...prev, { key: newSortCol, dir: newSortDir }]);
            }}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", background: "#fff" }}
          >
            Add
          </button>

          {sorts.length > 1 && (
            <>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {sorts.slice(1).map((s, idx) => (
                  <span
                    key={`${String(s.key)}-${idx}`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "4px 8px",
                      border: "1px solid #ddd",
                      borderRadius: 999,
                      background: "#f9fafb",
                    }}
                    title={`Secondary sort ${idx + 1}`}
                  >
                    <strong>{SORTABLE_COLUMNS.find((c) => c.key === s.key)?.label ?? String(s.key)}</strong>{" "}
                    {s.dir === "asc" ? "↑" : "↓"}
                    <button
                      onClick={() => removeSort(idx + 1)}
                      style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, marginLeft: 4, lineHeight: 1 }}
                      aria-label="Remove sort level"
                      title="Remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <button
                onClick={clearSorts}
                style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #ddd", background: "#fff" }}
              >
                Clear all
              </button>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Voter ID</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>First Name</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Middle Name</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Last Name</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Number</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Street</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>City</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>County</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>State</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>ZIP</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}>Precinct</th>
            <th style={{ textAlign: "left", borderBottom: "1px solid #ddd", padding: 8 }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.voter_id}`}>
              <td style={{ padding: 8 }}>{r.voter_id}</td>
              <td style={{ padding: 8 }}>{r.first_name ?? ""}</td>
              <td style={{ padding: 8 }}>{r.middle_name ?? ""}</td>
              <td style={{ padding: 8 }}>{r.last_name ?? ""}</td>
              <td style={{ padding: 8 }}>{displayNumber(r)}</td>
              <td style={{ padding: 8 }}>{displayStreet(r)}</td>
              <td style={{ padding: 8 }}>{r.city ?? ""}</td>
              <td style={{ padding: 8 }}>{r.county ?? ""}</td>
              <td style={{ padding: 8 }}>{r.state ?? ""}</td>
              <td style={{ padding: 8 }}>{r.zip ?? ""}</td>
              <td style={{ padding: 8 }}>{r.precinct ?? ""}</td>
              <td style={{ padding: 8 }}>
                <a
                  href={`/voters/${encodeURIComponent(r.voter_id)}?campaign=${encodeURIComponent(campaignId ?? "")}`}
                  style={{ color: "#2563eb" }}
                >
                  View
                </a>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={12} style={{ padding: 8, color: "#777" }}>
                {loading ? "Loading…" : "No results"}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Pagination (cursor) */}
      <div style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center" }}>
        <button
          disabled={pages.length <= 1}
          onClick={prevPage}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ddd",
            background: pages.length > 1 ? "#fff" : "#f3f4f6",
          }}
        >
          Prev
        </button>
        <div style={{ color: "#555" }}>
          Page {pages.length} {total != null && <span>&nbsp;•&nbsp; {total} total</span>}
        </div>
        <button
          disabled={rows.length < PAGE_SIZE}
          onClick={() => nextPage(rows[rows.length - 1]?.voter_id)}
          style={{
            padding: "6px 10px",
            borderRadius: 6,
            border: "1px solid #ddd",
            background: rows.length >= PAGE_SIZE ? "#fff" : "#f3f4f6",
          }}
        >
          Next
        </button>
      </div>
    </div>
  );
}
